import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Grid, Box, Button, CircularProgress, Typography } from '@mui/material';
import EditSiteForm from '../components/EditSiteForm';
import ExitToAppOutlinedIcon from '@mui/icons-material/ExitToAppOutlined';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { apiService } from '../services/apiService';

const Edit = () => {
    const { t } = useTranslation();
    const { userId } = useParams();
    const navigate = useNavigate();
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const isCreateButtonDisabled = false;

    const saveUserData = async () => {
        setIsSaving(true);

        try {
            await apiService.updateUser(userId, userData);
            setIsSaving(false);
            toast.success(t('toast_changes_saved'));
        } catch (error) {
            setIsSaving(false);
        }
    }

    useEffect(() => {
        const fetchUserById = async () => {
            try {
                const data = await apiService.getUserById(userId);
                if (!data) {
                    navigate('/');
                } else {
                    setUserData(data);
                }
            } catch (error) {
                console.error(t('error_get_user_by_slug'), error);
                navigate('/');
            } finally {
                setLoading(false);
            }
        };

        if (userId) fetchUserById();
    }, [userId, navigate]);

    if (loading) {
        return (
            <Grid container display="flex" justifyContent="center" alignItems="center" height="100vh">
                <CircularProgress />
            </Grid>
        );
    }

    return (
        <>
            <Header />
            <Grid container display="flex" justifyContent="center" alignItems="center" p={4}>
                <Grid item xs={12} sm={6}>

                    <Typography variant="h1" sx={{ fontSize: { xs: '2.125rem', sm: '3rem' }, fontWeight: 'bold', mb: 2 }} textAlign='center'>
                        {t('edit_page_title')}
                    </Typography>

                    <Grid item xs={12} display='flex' justifyContent='center' mb={8}>
                        <Button
                            variant="outlined"
                            component="span"
                            endIcon={<ExitToAppOutlinedIcon />}
                            onClick={() => navigate(`/u/${userData.slug}`)}
                            sx={{
                                borderColor: '#5947a6',
                                boxShadow: 'none',
                                '&:hover': {
                                    boxShadow: 'none',
                                },
                                color: '#5947a6',
                                fontSize: '0.9rem',
                                textAlign: 'center',
                                px: 3
                            }}
                        >
                            {t('button_go_to_site_label')}
                        </Button>
                    </Grid>

                    <EditSiteForm initialValues={userData} onFormChange={(fv) => setUserData(fv)} />

                    <Button
                        color="primary"
                        disabled={isCreateButtonDisabled || isSaving}
                        fullWidth
                        onClick={saveUserData}
                        variant="contained"
                        sx={{
                            boxShadow: 'none',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            mt: 4,
                            position: 'relative'
                        }}
                    >
                        {isSaving ? (
                            <Box sx={{ p: 0 }}>
                                <CircularProgress color='white' size={24} />
                            </Box>
                        ) : (
                            <>
                                <span>{t('save')}</span>
                                {isCreateButtonDisabled && (
                                    <Typography color="#fff" variant="caption" sx={{ mt: -1 }}>
                                        {t('create_page_cta_missing_data')}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Button>
                </Grid>
            </Grid>
            <Footer />
        </>
    );
};

export default Edit;